import { useEffect, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import { listingStateSlice, useAppSelector } from '@app/app-state';
import {
    FilterCheckboxItem,
    FilterCheckboxSingleProps,
} from '@app/components/molecules/filter-checkbox-group/filter-checkbox-group.props';
import { FilterRadioItem } from '@app/components/molecules/filter-radio-group/filter-radio-group.props';
import { requestFilterCounts } from '@app/helpers/filter-counter-scheduler';
import { useListingSearch } from '@app/hooks/useListingSearch';

type CheckboxFilterCounter = {
    items: FilterCheckboxItem[];
    selectedValue: string[];
    getPredictedValue:
        | ((value: string, checked: boolean) => string[])
        | ((value: string, checked: boolean) => number | null);
};

type RadioFilterCounter = {
    items: FilterRadioItem[];
    selectedValue: string;
    getPredictedValue: (value: string) => number | null;
};

type SearchFilterCounterProps = {
    filterKey: string;
    prependCheckbox?: FilterCheckboxSingleProps;
    isEnabled?: boolean;
} & (CheckboxFilterCounter | RadioFilterCounter);

export function useSearchFilterCounter({
    items,
    filterKey,
    prependCheckbox,
    selectedValue,
    getPredictedValue,
    isEnabled,
}: SearchFilterCounterProps) {
    const isListingItemsLoaded = useAppSelector(
        listingStateSlice.selectors.selectIsListingItemsLoaded,
    );
    const { getFilterCountArgs, filters, currentPage } = useListingSearch();
    const [counters, setCounters] = useState<(number | undefined)[]>(
        Array(items.length).fill(undefined),
    );
    const [prependCheckboxCounter, setPrependCheckboxCounter] = useState<number | undefined>(
        undefined,
    );
    const prevSelectedValueRef = useRef<string[] | string>(selectedValue);
    const prevExcludedParamsRef = useRef({ page: currentPage, sort: filters.sort });

    useEffect(() => {
        if (!isEnabled || !isListingItemsLoaded) {
            return;
        }

        const prevSelected = prevSelectedValueRef.current;
        const isFilterChanged = !isEqual(selectedValue, prevSelected);
        prevSelectedValueRef.current = selectedValue;

        if (isFilterChanged) {
            return;
        }

        const prevPageSort = prevExcludedParamsRef.current;
        const isPageOrSortChanged =
            currentPage !== prevPageSort.page || filters.sort !== prevPageSort.sort;
        prevExcludedParamsRef.current = { page: currentPage, sort: filters.sort };

        if (isPageOrSortChanged) {
            // Skip update when only page or sorting were changed
            return;
        }

        const predictedValues = items.map(item =>
            Array.isArray(selectedValue)
                ? (getPredictedValue as CheckboxFilterCounter['getPredictedValue'])(
                      item.value,
                      true,
                  )
                : (getPredictedValue as RadioFilterCounter['getPredictedValue'])(item.value),
        );
        const argsList = predictedValues.map(predictedValue =>
            getFilterCountArgs({ [filterKey]: predictedValue }),
        );

        if (prependCheckbox) {
            argsList.push(
                getFilterCountArgs({ [prependCheckbox.filterKey]: prependCheckbox.item.value }),
            );
        }

        // Batched, deferred until the page is idle and fetched in a Web Worker;
        // all counters of this group are set at once
        return requestFilterCounts(argsList, counts => {
            setCounters(counts.slice(0, items.length));

            if (prependCheckbox) {
                setPrependCheckboxCounter(counts[items.length]);
            }
        });
    }, [
        currentPage,
        filterKey,
        filters.sort,
        getFilterCountArgs,
        getPredictedValue,
        isListingItemsLoaded,
        items,
        prependCheckbox,
        selectedValue,
    ]);

    return {
        counters,
        prependCheckboxCounter,
    };
}
